import { useState } from "react";
import type { HabitInput, PlanProposal } from "@habitos/shared";
import { describeDays, formatDay, formatDuration } from "../lib/format";

interface Props {
  proposal: PlanProposal;
  acceptLabel: string;
  busy: boolean;
  onAccept: (habits: HabitInput[]) => void;
  onRetry: () => void;
}

/** A long plan shows its first steps; the rest open on demand. */
const FIRST_STEPS = 20;

/** Shows what the AI proposed. Nothing is saved until the person accepts. */
export function PlanReview({ proposal, acceptLabel, busy, onAccept, onRetry }: Props) {
  const [off, setOff] = useState<Set<number>>(new Set());
  const [all, setAll] = useState(false);
  const chosen = proposal.steps.filter((_, i) => !off.has(i));
  const shown = all ? proposal.steps : proposal.steps.slice(0, FIRST_STEPS);

  const toggle = (i: number) =>
    setOff((cur) => {
      const next = new Set(cur);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  return (
    <div className="stack">
      <section className="card">
        <p className="summary">{proposal.summary}</p>
      </section>
      <h2 className="section">Pasos</h2>
      <ul className="list">
        {shown.map((step, i) => (
          <li key={i}>
            <label className={`step card ${off.has(i) ? "off" : ""}`} style={{ borderLeftColor: step.color }}>
              <input type="checkbox" checked={!off.has(i)} onChange={() => toggle(i)} />
              <div className="grow">
                <div className="habit-title">{step.emoji} {step.title}</div>
                <div className="muted small">
                  {step.time} · {describeDays(step.daysOfWeek)} · {formatDuration(step.durationMinutes)}
                </div>
                <div className="muted small">
                  {formatDay(step.startDate)} → {step.endDate ? formatDay(step.endDate) : "en adelante"}
                </div>
                {step.why && <p className="small step-why">{step.why}</p>}
              </div>
            </label>
          </li>
        ))}
      </ul>
      {shown.length < proposal.steps.length && (
        <button className="btn" onClick={() => setAll(true)}>
          Ver los {proposal.steps.length - shown.length} pasos restantes
        </button>
      )}
      <p className="muted small">Después puedes ajustar cualquier paso desde Hábitos.</p>
      <div className="row gap">
        <button className="btn" onClick={onRetry} disabled={busy}>Otra propuesta</button>
        <button
          className="btn primary grow"
          disabled={busy || chosen.length === 0}
          onClick={() => onAccept(chosen.map(({ why: _why, ...habit }) => habit))}
        >
          {busy ? "Guardando…" : proposal.steps.length > FIRST_STEPS ? `${acceptLabel} (${chosen.length})` : acceptLabel}
        </button>
      </div>
    </div>
  );
}

export function Thinking({ text }: { text: string }) {
  return (
    <div className="card thinking" aria-busy="true">
      <span style={{ fontSize: "2rem" }}>✨</span>
      <p className="muted">{text}</p>
    </div>
  );
}
